import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { SEOHead, SEO_CONFIG } from '../components/SEOHead';
import { 
  Phone, MessageCircle, MapPin, Clock, Star, Send, CheckCircle,
  Navigation, Mail
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const businessHours = [
  { day: 'Monday', hours: '8:00 AM - 8:00 PM' },
  { day: 'Tuesday', hours: '8:00 AM - 8:00 PM' },
  { day: 'Wednesday', hours: '8:00 AM - 8:00 PM' },
  { day: 'Thursday', hours: '8:00 AM - 8:00 PM' },
  { day: 'Friday', hours: '8:00 AM - 8:00 PM' },
  { day: 'Saturday', hours: '8:00 AM - 8:00 PM' },
  { day: 'Sunday', hours: 'Closed' },
];

export const Contact = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service_needed: '',
    location: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    // Pre-fill service if passed from another page
    if (location.state?.service) {
      setFormData(prev => ({ ...prev, service_needed: location.state.service }));
    }
    if (location.state?.product) {
      setFormData(prev => ({ 
        ...prev, 
        service_needed: `Product Enquiry: ${location.state.product}`,
        message: `I am interested in ${location.state.product}. Please share more details and pricing.`
      }));
    }
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone) {
      toast.error('Please fill in your name and phone number');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/enquiries`, {
        ...formData,
        source: 'contact_form'
      });
      
      setIsSubmitted(true);
      toast.success('Thank you! We will contact you soon.');
      setFormData({
        name: '',
        phone: '',
        service_needed: '',
        location: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try calling us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const whatsappUrl = `https://wa.me/918512853955?text=${encodeURIComponent('Hi, I want to enquire about your aluminium and glass services.')}`;
  const mapsUrl = 'https://www.google.com/maps/search/Raj+Aluminum+Glass+Hardware+Ballabhgarh+Faridabad';

  return (
    <div data-testid="contact-page">
      <SEOHead {...SEO_CONFIG.contact} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-slate-900">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <p className="text-orange-400 font-semibold mb-4">GET IN TOUCH</p>
          <h1 className="font-['Oswald'] text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            Contact Us
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
            Ready to start your project? Get in touch with us for a free consultation and quote.
            We're here to help bring your vision to life.
          </p>
          
          {/* Quick Action Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:08512853955">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="contact-call-btn">
                <Phone className="w-5 h-5 mr-2" />
                Call Now
              </Button>
            </a>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[#25d366] hover:bg-[#20bd5a] text-white" data-testid="contact-whatsapp-btn">
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp
              </Button>
            </a>
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10" data-testid="contact-directions-btn">
                <Navigation className="w-5 h-5 mr-2" />
                Get Directions
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="py-20 bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="font-['Oswald'] text-3xl font-bold text-slate-900 mb-8">
                Contact Information
              </h2>

              <div className="space-y-6">
                {/* Business Name */}
                <Card className="border-l-4 border-l-orange-500">
                  <CardContent className="p-6">
                    <h3 className="font-['Oswald'] font-bold text-xl text-slate-900 mb-1">
                      Raj Aluminum & Glass Hardware
                    </h3>
                    <p className="text-slate-600 text-sm">Premium Aluminium & Glass Solutions</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <span className="text-sm text-slate-600">5.0 Google Rating</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Phone */}
                <a href="tel:08512853955" className="block">
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                    <CardContent className="p-6 flex items-start gap-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600">
                        <Phone className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Phone</p>
                        <p className="font-semibold text-slate-900 text-lg">085128 53955</p>
                        <p className="text-sky-600 text-sm mt-1">Click to call</p>
                      </div>
                    </CardContent>
                  </Card>
                </a>

                {/* Address */}
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="block">
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                    <CardContent className="p-6 flex items-start gap-4">
                      <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center text-sky-600">
                        <MapPin className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Address</p>
                        <p className="font-semibold text-slate-900">
                          Chungi no:- 5, Mohana Rd, near Bikaner sweets
                        </p>
                        <p className="text-slate-600">Uncha Gaon, Ballabhgarh, Faridabad</p>
                        <p className="text-slate-600">Haryana - 121004</p>
                        <p className="text-sky-600 text-sm mt-1">Click for directions</p>
                      </div>
                    </CardContent>
                  </Card>
                </a>

                {/* Business Hours */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-green-600">
                        <Clock className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Business Hours</p>
                        <p className="font-semibold text-slate-900">Mon - Sat: 8 AM - 8 PM</p>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      {businessHours.map((item) => (
                        <div key={item.day} className="flex justify-between">
                          <span className="text-slate-600">{item.day}</span>
                          <span className={item.hours === 'Closed' ? 'text-red-500' : 'text-slate-900'}>
                            {item.hours}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="contact-form shadow-xl">
                <CardContent className="p-8">
                  <h2 className="font-['Oswald'] text-2xl font-bold text-slate-900 mb-2">
                    Send Us a Message
                  </h2>
                  <p className="text-slate-600 mb-6">
                    Fill out the form below and we'll get back to you within 24 hours.
                  </p>

                  {isSubmitted ? (
                    <div className="text-center py-12" data-testid="form-success">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="font-['Oswald'] text-xl font-bold text-slate-900 mb-2">
                        Thank You!
                      </h3>
                      <p className="text-slate-600 mb-6">
                        Your enquiry has been received. We will contact you soon.
                      </p>
                      <Button onClick={() => setIsSubmitted(false)} variant="outline">
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                      <div>
                        <Label htmlFor="name">Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Your full name"
                          required
                          data-testid="input-name"
                        />
                      </div>

                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Your phone number"
                          required
                          data-testid="input-phone"
                        />
                      </div>

                      <div>
                        <Label htmlFor="service_needed">Service Needed</Label>
                        <Input
                          id="service_needed"
                          name="service_needed"
                          value={formData.service_needed}
                          onChange={handleChange}
                          placeholder="e.g., Aluminium Partition, Glass Railing"
                          data-testid="input-service"
                        />
                      </div>

                      <div>
                        <Label htmlFor="location">Your Location</Label>
                        <Input
                          id="location"
                          name="location"
                          value={formData.location}
                          onChange={handleChange}
                          placeholder="e.g., Faridabad, Ballabhgarh"
                          data-testid="input-location"
                        />
                      </div>

                      <div>
                        <Label htmlFor="message">Message</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us about your requirements..."
                          rows={4}
                          data-testid="input-message"
                        />
                      </div>

                      <Button 
                        type="submit" 
                        size="lg" 
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                        disabled={isSubmitting}
                        data-testid="submit-btn"
                      >
                        {isSubmitting ? (
                          'Sending...'
                        ) : (
                          <>
                            <Send className="w-5 h-5 mr-2" />
                            Send Enquiry
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-slate-50" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="font-['Oswald'] text-3xl font-bold text-slate-900 mb-4">
              Find Us on Map
            </h2>
            <p className="text-slate-600">
              Visit our shop in Ballabhgarh, Faridabad for personalized consultation.
            </p>
          </div>

          <div className="map-container rounded-2xl overflow-hidden shadow-xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3509.5!2d77.32!3d28.35!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDIxJzAwLjAiTiA3N8KwMTknMTIuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Raj Aluminum Location Map"
            ></iframe>
          </div>

          <div className="text-center mt-6">
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white">
                <Navigation className="w-5 h-5 mr-2" />
                Open in Google Maps
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
